/**
 * i18n resources for the bookings feature slice (namespace 'bookings').
 *
 * Self-contained so the route registers its own strings without touching the
 * shared platform aggregator. No hard-coded copy in components.
 */

/** Recursively widen string literals to `string` so each locale shares a shape. */
type Stringify<T> = { [K in keyof T]: T[K] extends string ? string : Stringify<T[K]> };

const en = {
  title: 'Bookings',
  description:
    'Bookings drive billing and PMS confirmation. Submit drafts for approval, confirm what is final and cancel when needed.',
  eyebrow: 'Hospitality',
  loading: 'Loading bookings…',
  error: {
    title: 'Could not load bookings',
    description: 'Something went wrong while loading bookings. Please try again.',
    retry: 'Try again',
  },
  empty: {
    title: 'No bookings yet',
    description: 'Bookings created from reservations will appear here.',
  },
  emptyFiltered: {
    title: 'No matching bookings',
    description: 'No bookings match these filters. Try clearing them.',
  },
  toolbar: {
    searchPlaceholder: 'Search id / room / PMS ref',
    statusAll: 'All statuses',
    modeAll: 'All modes',
    fromLabel: 'Check-in from',
    toLabel: 'Check-in to',
    clear: 'Clear filters',
  },
  paging: {
    prev: 'Previous',
    next: 'Next',
    perPage: 'Rows per page',
    range: '{{from}}–{{to}} of {{total}}',
  },
  status: {
    requested: 'Requested',
    draft: 'Draft',
    pending_staff_approval: 'Pending approval',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    failed: 'Failed',
    expired: 'Expired',
  },
  confirmationMode: {
    label: 'Confirmation',
    manual: 'Manual',
    pms: 'PMS',
  },
  list: {
    guest: 'Booking',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    roomType: 'Room type',
    status: 'Status',
    open: 'Open booking',
    notProvided: '—',
  },
  detail: {
    title: 'Booking detail',
    overview: 'Overview',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    roomType: 'Room type',
    externalPmsId: 'PMS reference',
    reservation: 'Linked reservation',
    notProvided: 'Not set',
    close: 'Close',
  },
  actions: {
    label: 'Actions',
    submit: 'Submit for approval',
    submitting: 'Submitting…',
    confirm: 'Confirm booking',
    confirming: 'Confirming…',
    cancel: 'Cancel booking',
    cancelling: 'Cancelling…',
    none: 'No actions available for this status.',
    submitted: 'Booking submitted for approval.',
    confirmed: 'Booking confirmed.',
    cancelled: 'Booking cancelled.',
    conflict: 'This action is no longer valid — the booking state changed. Refresh and try again.',
    error: 'The action could not be completed. Please try again.',
  },
  confirmDialog: {
    confirmTitle: 'Confirm this booking?',
    confirmBody:
      'Confirming is billing-affecting and may trigger a PMS write. This cannot be undone here.',
    cancelTitle: 'Cancel this booking?',
    cancelBody: 'Cancelling releases the booking. The guest may need to be notified separately.',
    proceed: 'Proceed',
    dismiss: 'Keep booking',
  },
};

type Resources = Stringify<typeof en>;

const bg: Resources = {
  title: 'Резервации (booking)',
  description:
    'Резервациите водят до таксуване и потвърждение в PMS. Подайте чернови за одобрение, потвърдете финалните и отказвайте при нужда.',
  eyebrow: 'Гостоприемство',
  loading: 'Зареждане на резервациите…',
  error: {
    title: 'Резервациите не можаха да се заредят',
    description: 'Възникна грешка при зареждането. Моля, опитайте отново.',
    retry: 'Опитайте отново',
  },
  empty: {
    title: 'Все още няма резервации',
    description: 'Резервациите, създадени от заявки, ще се появят тук.',
  },
  emptyFiltered: {
    title: 'Няма съвпадащи резервации',
    description: 'Няма резервации, отговарящи на филтрите. Опитайте да ги изчистите.',
  },
  toolbar: {
    searchPlaceholder: 'Търси id / стая / PMS реф.',
    statusAll: 'Всички статуси',
    modeAll: 'Всички режими',
    fromLabel: 'Настаняване от',
    toLabel: 'Настаняване до',
    clear: 'Изчисти филтрите',
  },
  paging: {
    prev: 'Назад',
    next: 'Напред',
    perPage: 'Реда на страница',
    range: '{{from}}–{{to}} от {{total}}',
  },
  status: {
    requested: 'Заявена',
    draft: 'Чернова',
    pending_staff_approval: 'Чака одобрение',
    confirmed: 'Потвърдена',
    cancelled: 'Отказана',
    failed: 'Неуспешна',
    expired: 'Изтекла',
  },
  confirmationMode: {
    label: 'Потвърждение',
    manual: 'Ръчно',
    pms: 'PMS',
  },
  list: {
    guest: 'Резервация',
    checkIn: 'Настаняване',
    checkOut: 'Напускане',
    roomType: 'Тип стая',
    status: 'Статус',
    open: 'Отвори резервацията',
    notProvided: '—',
  },
  detail: {
    title: 'Детайли на резервацията',
    overview: 'Преглед',
    checkIn: 'Настаняване',
    checkOut: 'Напускане',
    roomType: 'Тип стая',
    externalPmsId: 'PMS референция',
    reservation: 'Свързана заявка',
    notProvided: 'Не е зададено',
    close: 'Затвори',
  },
  actions: {
    label: 'Действия',
    submit: 'Подай за одобрение',
    submitting: 'Подаване…',
    confirm: 'Потвърди резервацията',
    confirming: 'Потвърждаване…',
    cancel: 'Откажи резервацията',
    cancelling: 'Отказване…',
    none: 'Няма налични действия за този статус.',
    submitted: 'Резервацията е подадена за одобрение.',
    confirmed: 'Резервацията е потвърдена.',
    cancelled: 'Резервацията е отказана.',
    conflict: 'Това действие вече не е валидно — статусът се промени. Опреснете и опитайте отново.',
    error: 'Действието не можа да се изпълни. Моля, опитайте отново.',
  },
  confirmDialog: {
    confirmTitle: 'Да потвърдите ли тази резервация?',
    confirmBody:
      'Потвърждаването влияе на таксуването и може да задейства запис в PMS. Това не може да се отмени тук.',
    cancelTitle: 'Да откажете ли тази резервация?',
    cancelBody:
      'Отказването освобождава резервацията. Гостът може да трябва да бъде уведомен отделно.',
    proceed: 'Продължи',
    dismiss: 'Запази резервацията',
  },
};

const de: Resources = {
  title: 'Buchungen',
  description:
    'Buchungen lösen Abrechnung und PMS-Bestätigung aus. Entwürfe einreichen, Endgültiges bestätigen und bei Bedarf stornieren.',
  eyebrow: 'Gastgewerbe',
  loading: 'Buchungen werden geladen…',
  error: {
    title: 'Buchungen konnten nicht geladen werden',
    description: 'Beim Laden ist etwas schiefgelaufen. Bitte erneut versuchen.',
    retry: 'Erneut versuchen',
  },
  empty: {
    title: 'Noch keine Buchungen',
    description: 'Aus Reservierungen erstellte Buchungen erscheinen hier.',
  },
  emptyFiltered: {
    title: 'Keine passenden Buchungen',
    description: 'Keine Buchungen entsprechen diesen Filtern. Filter zurücksetzen.',
  },
  toolbar: {
    searchPlaceholder: 'Suche id / Zimmer / PMS-Ref.',
    statusAll: 'Alle Status',
    modeAll: 'Alle Modi',
    fromLabel: 'Anreise ab',
    toLabel: 'Anreise bis',
    clear: 'Filter zurücksetzen',
  },
  paging: {
    prev: 'Zurück',
    next: 'Weiter',
    perPage: 'Zeilen pro Seite',
    range: '{{from}}–{{to}} von {{total}}',
  },
  status: {
    requested: 'Angefragt',
    draft: 'Entwurf',
    pending_staff_approval: 'Genehmigung ausstehend',
    confirmed: 'Bestätigt',
    cancelled: 'Storniert',
    failed: 'Fehlgeschlagen',
    expired: 'Abgelaufen',
  },
  confirmationMode: {
    label: 'Bestätigung',
    manual: 'Manuell',
    pms: 'PMS',
  },
  list: {
    guest: 'Buchung',
    checkIn: 'Anreise',
    checkOut: 'Abreise',
    roomType: 'Zimmertyp',
    status: 'Status',
    open: 'Buchung öffnen',
    notProvided: '—',
  },
  detail: {
    title: 'Buchungsdetails',
    overview: 'Übersicht',
    checkIn: 'Anreise',
    checkOut: 'Abreise',
    roomType: 'Zimmertyp',
    externalPmsId: 'PMS-Referenz',
    reservation: 'Verknüpfte Reservierung',
    notProvided: 'Nicht gesetzt',
    close: 'Schließen',
  },
  actions: {
    label: 'Aktionen',
    submit: 'Zur Genehmigung einreichen',
    submitting: 'Wird eingereicht…',
    confirm: 'Buchung bestätigen',
    confirming: 'Wird bestätigt…',
    cancel: 'Buchung stornieren',
    cancelling: 'Wird storniert…',
    none: 'Für diesen Status sind keine Aktionen verfügbar.',
    submitted: 'Buchung zur Genehmigung eingereicht.',
    confirmed: 'Buchung bestätigt.',
    cancelled: 'Buchung storniert.',
    conflict:
      'Diese Aktion ist nicht mehr gültig — der Status hat sich geändert. Aktualisieren und erneut versuchen.',
    error: 'Die Aktion konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
  },
  confirmDialog: {
    confirmTitle: 'Diese Buchung bestätigen?',
    confirmBody:
      'Die Bestätigung wirkt sich auf die Abrechnung aus und kann einen PMS-Schreibvorgang auslösen. Dies kann hier nicht rückgängig gemacht werden.',
    cancelTitle: 'Diese Buchung stornieren?',
    cancelBody:
      'Die Stornierung gibt die Buchung frei. Der Gast muss ggf. separat benachrichtigt werden.',
    proceed: 'Fortfahren',
    dismiss: 'Buchung behalten',
  },
};

const fr: Resources = {
  title: 'Réservations',
  description:
    'Les réservations déclenchent la facturation et la confirmation PMS. Soumettez les brouillons, confirmez le définitif et annulez au besoin.',
  eyebrow: 'Hôtellerie',
  loading: 'Chargement des réservations…',
  error: {
    title: 'Impossible de charger les réservations',
    description: 'Une erreur est survenue lors du chargement. Veuillez réessayer.',
    retry: 'Réessayer',
  },
  empty: {
    title: 'Aucune réservation pour le moment',
    description: 'Les réservations créées à partir des demandes apparaîtront ici.',
  },
  emptyFiltered: {
    title: 'Aucune réservation correspondante',
    description: 'Aucune réservation ne correspond à ces filtres. Essayez de les effacer.',
  },
  toolbar: {
    searchPlaceholder: 'Rechercher id / chambre / réf. PMS',
    statusAll: 'Tous les statuts',
    modeAll: 'Tous les modes',
    fromLabel: 'Arrivée à partir du',
    toLabel: "Arrivée jusqu'au",
    clear: 'Effacer les filtres',
  },
  paging: {
    prev: 'Précédent',
    next: 'Suivant',
    perPage: 'Lignes par page',
    range: '{{from}}–{{to}} sur {{total}}',
  },
  status: {
    requested: 'Demandée',
    draft: 'Brouillon',
    pending_staff_approval: 'En attente de validation',
    confirmed: 'Confirmée',
    cancelled: 'Annulée',
    failed: 'Échouée',
    expired: 'Expirée',
  },
  confirmationMode: {
    label: 'Confirmation',
    manual: 'Manuelle',
    pms: 'PMS',
  },
  list: {
    guest: 'Réservation',
    checkIn: 'Arrivée',
    checkOut: 'Départ',
    roomType: 'Type de chambre',
    status: 'Statut',
    open: 'Ouvrir la réservation',
    notProvided: '—',
  },
  detail: {
    title: 'Détail de la réservation',
    overview: 'Aperçu',
    checkIn: 'Arrivée',
    checkOut: 'Départ',
    roomType: 'Type de chambre',
    externalPmsId: 'Référence PMS',
    reservation: 'Demande liée',
    notProvided: 'Non défini',
    close: 'Fermer',
  },
  actions: {
    label: 'Actions',
    submit: 'Soumettre pour validation',
    submitting: 'Soumission…',
    confirm: 'Confirmer la réservation',
    confirming: 'Confirmation…',
    cancel: 'Annuler la réservation',
    cancelling: 'Annulation…',
    none: 'Aucune action disponible pour ce statut.',
    submitted: 'Réservation soumise pour validation.',
    confirmed: 'Réservation confirmée.',
    cancelled: 'Réservation annulée.',
    conflict: "Cette action n'est plus valide — le statut a changé. Actualisez et réessayez.",
    error: "L'action n'a pas pu être effectuée. Veuillez réessayer.",
  },
  confirmDialog: {
    confirmTitle: 'Confirmer cette réservation ?',
    confirmBody:
      'La confirmation affecte la facturation et peut déclencher une écriture PMS. Cette action est irréversible ici.',
    cancelTitle: 'Annuler cette réservation ?',
    cancelBody:
      'L’annulation libère la réservation. Le client devra peut-être être averti séparément.',
    proceed: 'Continuer',
    dismiss: 'Conserver la réservation',
  },
};

export const namespace = 'bookings' as const;

export const resources = { en, bg, de, fr } as const;

export type BookingsResources = Resources;
